import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Headset, Store, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const OPTIONS = [
  {
    value: "receptionist",
    label: "Receptionist Salon Up",
    icon: Headset,
    desc: "I nuovi lead vengono presi in carico dalla receptionist dell'agenzia, dai suoi canali personali (WhatsApp e telefono della receptionist).",
  },
  {
    value: "salon",
    label: "Salone",
    icon: Store,
    desc: "I nuovi lead vengono gestiti direttamente dai canali del salone (WhatsApp e telefono del tenant).",
  },
];

export const AcquisitionChannelSection = () => {
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/agency/settings/acquisition"),
        api.get("/agency/settings/acquisition/readiness"),
      ]);
      setCurrent(s.data.acquisition_channel);
      setSelected(s.data.acquisition_channel);
      setReadiness(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/agency/settings/acquisition", { acquisition_channel: selected });
      setCurrent(r.data.acquisition_channel);
      toast.success("Canale di acquisizione aggiornato: si applica solo ai nuovi lead");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  if (current === null) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground" data-testid="acquisition-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
      </div>
    );
  }

  const dirty = selected !== current;

  return (
    <div className="space-y-6" data-testid="acquisition-channel-section">
      <div>
        <h2 className="text-lg font-semibold">Canale di acquisizione</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Definisce chi gestisce la fase di <strong>acquisizione</strong> (prima della
          prenotazione) dei nuovi lead, per tutti i saloni con servizio receptionist attivo.
          Dopo la creazione dell'appuntamento, promemoria e conferme restano sempre sul
          WhatsApp del salone.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {OPTIONS.map((o) => {
          const Icon = o.icon;
          const active = selected === o.value;
          return (
            <button
              type="button"
              key={o.value}
              onClick={() => setSelected(o.value)}
              data-testid={`acquisition-option-${o.value}`}
              className={`text-left rounded-xl border p-4 transition-colors ${
                active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-primary" : "border-muted-foreground"}`}>
                  {active && <span className="h-2 w-2 rounded-full bg-primary" />}
                </span>
                <Icon className="h-4 w-4" />
                <span className="font-medium text-sm">{o.label}</span>
                {current === o.value && (
                  <span className="ml-auto text-xs text-muted-foreground" data-testid={`acquisition-current-${o.value}`}>
                    attuale
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{o.desc}</p>
            </button>
          );
        })}
      </div>

      {dirty && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2"
             data-testid="acquisition-change-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Questa modifica verrà applicata solo ai nuovi lead. Le conversazioni già aperte
            continueranno a utilizzare il canale con cui sono state avviate.
          </span>
        </div>
      )}

      <button
        type="button"
        disabled={!dirty || saving}
        onClick={save}
        data-testid="acquisition-save-btn"
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva canale
      </button>

      {readiness && (
        <div className="rounded-xl border p-4 space-y-3" data-testid="acquisition-readiness-card">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Preflight modalità receptionist</h3>
            <span className="text-xs text-muted-foreground" data-testid="acquisition-readiness-count">
              {readiness.receptionists_whatsapp_ready}/{readiness.receptionists_total} receptionist con WhatsApp collegato
            </span>
          </div>

          {readiness.tenants_without_receptionist?.length > 0 ? (
            <div className="text-xs text-amber-800 flex gap-2" data-testid="readiness-warn-tenants">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Saloni senza receptionist assegnata:{" "}
                {readiness.tenants_without_receptionist.map((t) => t.name || t.tenant_id).join(", ")}
              </span>
            </div>
          ) : (
            <div className="text-xs text-emerald-700 flex gap-2" data-testid="readiness-ok-tenants">
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" /> Tutti i saloni con servizio attivo hanno una receptionist assegnata
            </div>
          )}

          <div className="space-y-1" data-testid="readiness-receptionists">
            {(readiness.receptionists || []).map((r) => (
              <div key={r.receptionist_user_id} className="flex flex-wrap items-center gap-2 text-xs"
                   data-testid={`readiness-row-${r.receptionist_user_id}`}>
                <span className="font-medium">{r.name}</span>
                <Badge ok={r.whatsapp_connected} label="WhatsApp" required />
                <Badge ok={r.voice_verified} label="Voce" />
                <Badge ok={r.google_connected} label="Google" />
              </div>
            ))}
            {(readiness.receptionists || []).length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="readiness-empty">
                Nessuna receptionist assegnata.
              </p>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground">
            WhatsApp collegato è il requisito necessario per la modalità receptionist. Voce e
            Google Calendar sono moduli indipendenti: un problema sulla telefonia non blocca
            WhatsApp o l'AI, e viceversa. Nessun fallback automatico verrà applicato: se la
            configurazione è incompleta il lead non viene dirottato sul salone.
          </p>
        </div>
      )}
    </div>
  );
};

const Badge = ({ ok, label, required }) => (
  <span className={`rounded-full px-2 py-0.5 border ${
    ok ? "border-emerald-300 bg-emerald-50 text-emerald-700"
       : required ? "border-red-300 bg-red-50 text-red-700"
                  : "border-amber-300 bg-amber-50 text-amber-800"}`}>
    {label}: {ok ? "ok" : "mancante"}
  </span>
);

export default AcquisitionChannelSection;
